import React, { useContext, useLayoutEffect, useMemo } from 'react';
import { getLocale, shallowEqual, useSelector } from 'umi';
import OverviewMetricsChart from '../OverviewMetricsChart';
import { FilterContextType } from '@/pages/dashboard/components/PageContainer/type';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { LANGUAGE_EN } from '@home/_config/config';
import { dynamicString } from '@home/utils/dynamicString';
import { getName } from '@home/utils/common';
import { ITabConfig, IApiConfig, IChartConfig, IFormValue } from '../../_config/_type';
import { IFormatRules } from '@/pages/dashboard/utils/dataFormatter';
import ViewportFullscreenShell from '@/pages/dashboard/components/ViewportFullscreen';
import type { IViewportFullscreenApi } from '@/pages/dashboard/components/ViewportFullscreen/types';
import KpiEventFilter, {
  type IChartEventSetting,
  type IKpiEventFilterLanguage,
} from '@/pages/dashboard/filterPageComponents/overview/KpiEventFilter';
import FsEventWindows from './FsEventWindows';
import type { IChartOverlaySlotContext } from '../OverviewMetricsChart/type';
import type {
  EventBubbleDetailsNavConfig,
  TEventTypeBarColorMapConfig,
} from '@/pages/dashboard/components/EventMetricTrend/types';
import { useFsEventEnhancements } from './hooks';
import './index.scss';
import { ICommonUiProps } from '@/pages/dashboard/types/dashboard';

// 组件配置接口
interface IComponentMetric {
  enName: string;
  cnName: string;
  metricKey: string;
  type: string;
  rainbowKey: string;
  explainEn: string;
  explainCn: string;
  [key: string]: unknown;
}

/** 全屏增强配置：默认全部为 true，可在 pageConfig 关闭其中某项 */
export interface IFullScreenEnhancements {
  /** 全屏时在累计值下拉右侧展示 KpiEventFilter；默认 true */
  showEventFilter?: boolean;
  /**
   * 全屏时事件筛选器紧贴指标标题右侧（间距 20px）；默认 false，仍放在维度下拉右侧。
   * Revenue 等页面在 pageConfig 中显式设为 true。
   */
  eventFilterBesideTitle?: boolean;
  /** 全屏时在图表上方展示 EventWindows 事件浮窗；默认 true；受 KpiEventFilter showEventBubbles 控制 */
  showEventWindows?: boolean;
  /**
   * 全屏时是否展示上方指标 Segment（日 / WD2 / WD7 等 tab）；默认 false（不展示）。
   * 需在 pageConfig.fullScreenEnhancements 显式设为 true 才开启。
   */
  showSegmentTabsInFullScreen?: boolean;
  /**
   * 全屏内不展示的事件类型（与 event_type 的 name_cn / name_en / name 或分组 key trim 后全等）。
   * @example ["Holiday", "实验"]
   */
  excludeEventTypes?: string[];
  /**
   * 全屏事件设置状态在退出全屏时是否保留；默认 true。
   * false 时退出全屏会重置 KpiEventFilter 全部状态（含弹窗勾选、显示事件开关等）。
   */
  persistEventSetting?: boolean;
  /** @deprecated 请使用 config.eventTypeMaxCount（仅非全屏生效） */
  eventTypeMaxCount?: number;
  /** 透传给 KpiEventFilter 的中英文文案 */
  eventFilterLanguage?: Partial<IKpiEventFilterLanguage>;
  /**
   * 事件设置弹窗的事件分组数据 dataKey；从 props.data[dataKey] 读取按 event_type 分组的事件列表。
   * 不传时弹窗无数据，点击设置图标仅打开空弹窗。
   */
  eventModalDataKey?: string;
  /** 事件气泡点击跳转事件分析详情（与 EventMetricTrend chartConfig.eventBubbleDetailsNav 一致） */
  eventBubbleDetailsNav?: EventBubbleDetailsNavConfig;
  /**
   * EventWindows 每条事件前方矩形色条（写入 event_bar_color，与 KPI chartConfig.eventTypeBarColorMap 一致）。
   * 未配置时使用舆情 EventItem 默认蓝色条。
   */
  eventTypeBarColorMap?: TEventTypeBarColorMapConfig;
}

// 组件配置接口
interface IConfig extends IChartConfig {
  componentMetrics: IComponentMetric[];
  api: IApiConfig;
  renderIndex?: number;
  excelFilterEnName?: string;
  excelFilterCnName?: string;
  isMoblie?: boolean;
  showLastMonthYear?: boolean;
  formatRules?: IFormatRules[];
  /** 是否启用视口全屏能力；为 true 时由 ViewportFullscreenShell 包裹并注入 viewportFullscreen */
  enableFullScreen?: boolean;
  /** 全屏增强配置；不配置时不展示任何增强 UI（向后兼容） */
  fullScreenEnhancements?: IFullScreenEnhancements;
  /**
   * 非全屏 EventDetailsPop：每种事件类型最多展示条数；默认 3。
   * 全屏 EventWindows 不限制条数。
   * @deprecated 请写在 config 根级；fullScreenEnhancements.eventTypeMaxCount 仍兼容读取
   */
  eventTypeMaxCount?: number;
}

// Props 接口
interface IOverviewMetricsTrendCoProps extends ICommonUiProps {
  config: IConfig;
  eventData?: unknown[];
  loading?: boolean;
  data?: {
    trendMetrics?: string[];
    time_zone?: string;
    [key: string]: unknown;
  };
}

/**
 * 退出全屏时重置事件筛选。
 * prevIsFullScreenRef 必须由 OverviewMetricsTrendCo 持有：Portal 切换容器时子树可能 remount，
 * 若 ref 在 Guard 内部会在 remount 时丢失「上一帧全屏」导致 persistEventSetting=false 不生效。
 */
function FsEventSettingResetGuard(props: {
  isFullScreen: boolean;
  persistEventSetting: boolean;
  prevIsFullScreenRef: React.MutableRefObject<boolean>;
  onReset: () => void;
}) {
  const { isFullScreen, persistEventSetting, prevIsFullScreenRef, onReset } =
    props;

  useLayoutEffect(() => {
    if (prevIsFullScreenRef.current && !isFullScreen && !persistEventSetting) {
      onReset();
    }
    prevIsFullScreenRef.current = isFullScreen;
  }, [isFullScreen, onReset, persistEventSetting, prevIsFullScreenRef]);

  return null;
}

export default function OverviewMetricsTrendCo(props: IOverviewMetricsTrendCoProps) {
  const { config, eventData = [], loading, data, className } = props;
  const { componentMetrics, api, renderIndex = 0, excelFilterEnName = '', excelFilterCnName = '', isMoblie = false, showLastMonthYear = false, formatRules,
    visibleLegends, enableFullScreen = false, fullScreenEnhancements, eventTypeMaxCount: eventTypeMaxCountCfg,
  } = config;
  const isEN = getLocale() === LANGUAGE_EN;
  const timeZone = parseInt(props?.data?.time_zone || '', 10) || 0;
  const contextObj: FilterContextType = useContext(FilterContext);
  const { filterVal: formValue, filterLoading } = contextObj || {};

  const {
    fsShowEventFilter,
    fsShowEventWindows,
    fsShowSegmentTabsInFullScreen,
    fsEventFilterBesideTitle,
    fsPersistEventSetting,
    eventTypeMaxCount,
    fsEventFilterLanguage,
    fsEventTypeBarColorMap,
    prevViewportFullScreenRef,
    eventBubbleDetailsNavCfg,
    fsEventDateType,
    fsEventDateFormat,
    fsXAxisData,
    handleFsXAxisDataChange,
    fullScreenEventData,
    fsEventModalData,
    eventDetailsNavForWindows,
    fsChartEventSetting,
    fsBubbleFilterCommitted,
    fsEventSettingResetKey,
    handleFsEventSettingChange,
    resetFsEventSetting,
  } = useFsEventEnhancements({
    fullScreenEnhancements,
    eventTypeMaxCountCfg,
    eventData,
    data: data as Record<string, unknown> | undefined,
    formValue: formValue as Record<string, unknown> | undefined,
  });

  interface IStoreState {
    dashboard?: {
      currentGame?: {
        game_code: string;
      };
      explainInfo: Record<string, unknown>;
    };
    dashboardSetting?: {
      gameConfig: Record<string, unknown>;
    };
  }
  
  const { gameCode, explainInfo, gameConfig } = useSelector((state: IStoreState) => ({
    gameCode: state?.dashboard?.currentGame?.game_code || '',
    explainInfo: state?.dashboard?.explainInfo || {},
    gameConfig: state?.dashboardSetting?.gameConfig || {},
  }), shallowEqual);

  const type = formValue?.timeType?.toUpperCase() || 'DAY';

  /**
   * 根据 trendMetrics 配置筛选并排序指标列表
   * @param metrics 原始指标配置数组
   * @param trendMetrics 需要筛选和排序的指标 key 数组
   * @returns 筛选并按 trendMetrics 顺序排列的指标数组
   */
  const filterAndSortComponentMetricsByTrendMetrics = (
    metrics: IComponentMetric[],
    trendMetrics?: string[]
  ): IComponentMetric[] => {
    // 未配置 trendMetrics 时,返回全部指标
    if (!trendMetrics || trendMetrics.length === 0) {
      return metrics;
    }

    // 创建指标 map 以便快速查找
    const metricsMap = new Map<string, IComponentMetric>();
    metrics.forEach((metric) => {
      const { metricKey } = metric;
      metricsMap.set(metricKey, metric);
    });

    // 按 trendMetrics 顺序筛选并返回指标
    return trendMetrics
      .map((key) => metricsMap.get(key))
      .filter((metric): metric is IComponentMetric => metric !== undefined);
  };

  const { tabConfig, intList, percentList, excelFilterName } = useMemo(() => {
    const intList: string[] = [];
    const percentList: string[] = [];
    
    // 根据 trendMetrics 配置筛选并排序指标
    const filteredMetrics = filterAndSortComponentMetricsByTrendMetrics(
      componentMetrics,
      data?.trendMetrics
    );
    
    const tabConfig: ITabConfig[] = filteredMetrics?.map((item) => {
      const { enName = '', cnName = '', metricKey = '', type = '', rainbowKey = '', explainEn = '', explainCn = '' } = item || {};
      type === 'int' && intList?.push(metricKey);
      type === 'percent' && percentList?.push(metricKey);
      // 类型断言: 将 explainInfo 断言为符合 getExplainVal 期望的类型
      const explain = getExplainVal(explainInfo as never, rainbowKey, getLocale(), explainEn, explainCn);
      const name = getName(isEN, dynamicString(enName, data as Record<string, string>), dynamicString(cnName, data as Record<string, string>));

      return {
        ...item,
        bubble: rainbowKey ? explain?.replaceAll(/(\\n|\n)/g, '<br/>') : '',
        text: name,
        value: metricKey,
      };
    }) || [];

    return {
      tabConfig,
      intList,
      percentList,
      excelFilterName: getName(isEN, excelFilterEnName, excelFilterCnName),
    };
  }, [componentMetrics, data, explainInfo, isEN]);

  /**
   * 全屏事件筛选：enableFullScreen 时始终挂载（非全屏由 chartItem 放入隐藏 keeper），
   * 避免退出全屏卸载 KpiEventFilter / EventSettingModal 导致勾选态被重置为默认全选。
   */
  const fsEventFilterSlot =
    enableFullScreen && fsShowEventFilter ? (
      <KpiEventFilter
        key={fsEventSettingResetKey}
        className="dbd-overview-metrics-trend-fs-event-filter"
        chartEventSetting={fsChartEventSetting}
        changeEventSetting={handleFsEventSettingChange}
        eventModalData={fsEventModalData}
        eventXAxisData={fsXAxisData}
        eventDateType={fsEventDateType}
        language={fsEventFilterLanguage}
        bubbleSelectionCommitted={fsBubbleFilterCommitted}
      />
    ) : undefined;

  const renderChart = (viewportFullscreen?: IViewportFullscreenApi): JSX.Element => {
    const isFs = !!(viewportFullscreen?.isFullScreen);
    /** 可见位置由 chartItem 按全屏态切换；节点本身不因 isFs 卸载 */
    const leftExtraSlot = fsEventFilterSlot;

    /**
     * 全屏 + showEventWindows 开启时，把 FsEventWindows 注入到 chartItem 的 .dbd-overlay-chart 内部，
     * 使其相对于 chart 容器绝对定位，而非相对于全屏 layer。
     */
    const chartOverlaySlot =
      isFs && fsShowEventWindows
        ? (ctx: IChartOverlaySlotContext) => (
          <FsEventWindows
            active={isFs}
            {...ctx}
            eventData={fullScreenEventData as unknown[]}
            chartEventSetting={fsChartEventSetting}
            bubbleFilterCommitted={fsBubbleFilterCommitted}
            onXAxisDataChange={handleFsXAxisDataChange}
            eventDetailsNav={eventDetailsNavForWindows}
            eventTypeBarColorMap={fsEventTypeBarColorMap}
            eventDateType={fsEventDateType}
            eventDateFormat={fsEventDateFormat}
          />
        )
        : undefined;

    return (
      <OverviewMetricsChart
        config={config}
        renderIndex={renderIndex}
        timeZone={timeZone}
        isMoblie={isMoblie}
        excelFilterName={excelFilterName}
        intList={intList}
        percentList={percentList}
        api={api}
        tabConfig={tabConfig}
        gameCode={gameCode}
        gameConfig={gameConfig}
        filterLoading={filterLoading}
        formValue={formValue as IFormValue}
        explainInfo={explainInfo}
        placeholderId={renderIndex === 0 ? 'overview-daily-key-metrics' : ''}
        type={type}
        dailyReqType={''}
        eventData={eventData}
        loading={loading}
        showLastMonthYear={showLastMonthYear}
        visibleLegends={visibleLegends}
        formatRules={formatRules}
        viewportFullscreen={viewportFullscreen}
        leftExtraSlot={leftExtraSlot}
        chartOverlaySlot={chartOverlaySlot}
        showSegmentTabsInFullScreen={fsShowSegmentTabsInFullScreen}
        eventFilterBesideTitle={fsEventFilterBesideTitle}
        eventTypeMaxCount={eventTypeMaxCount}
      />
    );
  };

  if (enableFullScreen) {
    return (
      <ViewportFullscreenShell
        enabled={true}
        className={`dbd-overview-metrics-trend-co ${className || ''}`}
        layerClassName="dbd-overview-metrics-trend-fs-root dbd-overview-metrics-trend-fs-root--active"
        backdropClassName="dbd-overview-metrics-trend-fs-backdrop"
        placeholderClassName="dbd-overview-metrics-trend-fs-placeholder"
        inlineHostClassName="dbd-overview-metrics-trend-fs-inline-host"
        placeholderMinHeight={420}
      >
        {(api: IViewportFullscreenApi) => (
          <>
            <FsEventSettingResetGuard
              isFullScreen={api.isFullScreen}
              persistEventSetting={fsPersistEventSetting}
              prevIsFullScreenRef={prevViewportFullScreenRef}
              onReset={resetFsEventSetting}
            />
            {renderChart(api)}
          </>
        )}
      </ViewportFullscreenShell>
    );
  }

  return (
    <div className={`dbd-overview-metrics-trend-co ${className || ''}`}>
      {renderChart()}
    </div>
  );
}